import { LoginError } from '../../../modules/login/services/login.service';
import { EditEmailUser } from '../edit-email/edit-email.component';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { Validators } from '@angular/forms';
import { I18nService } from '@ppl/i18n';
import { TFormBuilder } from '@ppl/ui/form';
import { KEYCODE_ENTER } from '@ppl/utils/keycode.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import { PASSWORD_MAX_LENGTH } from '@ppl/utils/password.utils';
import { PplValidators } from '@ppl/utils/validators.utils';
import type {
  AfterViewInit,
  OnChanges
} from '@angular/core';
import type { TFormGroup } from '@ppl/ui/form';
import type { TSimpleChanges } from '@ppl/utils/angular.utils';

@Component({
    selector: 'ppl-login-common',
    templateUrl: './login-common.component.html',
    styleUrls: ['./login-common.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginCommonComponent implements OnChanges, AfterViewInit {

  @Input() value: LoginCommonCredentials;
  @Input() description: string;
  @Input() error: LoginError;
  @Input() errorMessage: string;
  @Input() signUpUrl: string;

  @Input() mode: LoginCommonMode = LoginCommonMode.Email;

  @Input() titleEmailScreen = this.i18nService.translate('Sign_in');
  @Input() titlePasswordScreen = this.i18nService.translate('Welcome');
  @Input() googleShowLogin = true;
  @Input() googleLoginDisabled = false;
  @Input() microsoftShowLogin = true;
  @Input() loading = false;
  @Input() showCopyright = false;
  @Input() showRememberMe = true;

  @Output() valueChange = new EventEmitter<LoginCommonCredentials>();
  @Output() microsoftLogin = new EventEmitter();
  @Output() googleLogin = new EventEmitter();
  @Output() edit = new EventEmitter<LoginCommonCredentials>();

  @ViewChild('emailInput', { read: ElementRef, static: false }) emailInput: ElementRef;
  @ViewChild('passwordInput', { read: ElementRef, static: false }) passwordInput: ElementRef;

  authForm: TFormGroup<LoginCommonCredentials>;

  LoginCommonMode = LoginCommonMode;

  @MemoizeLast<LoginCommonComponent>(['description'])
  get descriptionText() {
    return this.description
      ? this.description
      : this.i18nService.translate('Continue_to_Coevera_CRM');
  }

  @MemoizeLast<LoginCommonComponent>(['value'])
  get editEmailUser(): EditEmailUser {
    return this.value
      ? { email: this.value.email, name: this.value?.name || '' }
      : { email: '', name: '' };
  }

  constructor(
    private fb: TFormBuilder,
    private i18nService: I18nService
  ) {
    this.authForm = this.createForm();
  }

  ngOnChanges(changes: TSimpleChanges<LoginCommonComponent>) {
    if (changes.value && changes.value.currentValue) {
      this.authForm.patchValue(changes.value.currentValue);
    }

    if (changes.mode && changes.mode.currentValue) {
      this.setMode(changes.mode.currentValue);
    }
  }

  ngAfterViewInit() {
    if (this.emailInput) {
      this.emailInput.nativeElement.blur();
    }
    if (this.passwordInput) {
      this.passwordInput.nativeElement.blur();
    }
  }

  onKeyUp(event) {
    if (event.which === KEYCODE_ENTER) {
      this.onSubmit();
    }
  }

  onSubmit() {
    this.authForm.markAllAsTouched();
    if (this.authForm.valid) {
      this.valueChange.emit(this.authForm.value);
    }
  }

  onEdit() {
    this.edit.emit(this.value);
  }

  createForm() {
    return this.fb.group<LoginCommonCredentials>({
      email: ['', [Validators.required, PplValidators.email]],
      password: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(PASSWORD_MAX_LENGTH)]],
      remember: [{ value: DEFAULT_REMEMBER_ME, disabled: true }]
    });
  }

  setMode(mode: LoginCommonMode) {
    const password = this.authForm.get('password');
    const remember = this.authForm.get('remember');
    if (mode === LoginCommonMode.Email) {
      password.setValue('');
      remember.setValue(DEFAULT_REMEMBER_ME);
      password.disable();
      remember.disable();
    } else {
      password.markAsUntouched();
      password.markAsPristine();
      password.enable();
      remember.enable();
    }
  }
}

export interface LoginCommonCredentials {
  email: string;
  name?: string;
  password: string;
  remember: boolean;
}

export enum LoginCommonMode {
  Email = 1,
  Password
}

const DEFAULT_REMEMBER_ME = true;
